/**
 * Global Schema Templates Page
 *
 * Admin-only page for managing global schema templates.
 * Features:
 * - View all schema templates with versions
 * - Create new templates
 * - Create new versions of existing templates
 * - Assign templates to organizations
 * - View template usage
 */

import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  Tag,
  AlertCircle,
  CheckCircle,
  Edit,
  Eye,
  Code,
  Box,
  MoreVertical,
  Trash2,
  Type,
} from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import schemaApi, {
  GlobalSchemaTemplate,
  TemplateUsage
} from '../../services/schemaApi';
import { VisualSchemaBuilder, VisualSchemaData } from '../../components/admin/VisualSchemaBuilder';
import * as yaml from 'js-yaml';

interface GlobalSchemaTemplatesProps {
  onNavigate: (page: string, id?: string) => void;
}

export const GlobalSchemaTemplates: React.FC<GlobalSchemaTemplatesProps> = ({ onNavigate }) => {
  const [templates, setTemplates] = useState<GlobalSchemaTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templateUsage, setTemplateUsage] = useState<TemplateUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalTemplateName, setModalTemplateName] = useState<string | null>(null);
  const [templateYaml, setTemplateYaml] = useState<string>('');
  const [editMode, setEditMode] = useState<'visual' | 'yaml'>('visual');
  const [visualSchemaData, setVisualSchemaData] = useState<VisualSchemaData>({
    classes: [],
    enums: [],
    categories: []
  });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [contextMenuTemplate, setContextMenuTemplate] = useState<string | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameTemplateName, setRenameTemplateName] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTemplateName, setDeleteTemplateName] = useState('');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTemplateName, setAssignTemplateName] = useState('');
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<Array<{ id: string; name: string }>>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Create Template Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTemplateName, setCreateTemplateName] = useState('');
  const [createTemplateTitle, setCreateTemplateTitle] = useState('');
  const [createTemplateDescription, setCreateTemplateDescription] = useState('');
  const [createTemplateTags, setCreateTemplateTags] = useState<string[]>([]);
  const [selectedBaseTemplate, setSelectedBaseTemplate] = useState<string>('');
  const [selectedImports, setSelectedImports] = useState<string[]>([]);
  const [createValidationErrors, setCreateValidationErrors] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      loadTemplateUsage(selectedTemplate);
    }
  }, [selectedTemplate]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenuTemplate) {
        setContextMenuTemplate(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [contextMenuTemplate]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await schemaApi.listTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      } else {
        setError(response.message || 'Failed to load templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateUsage = async (templateName: string) => {
    try {
      const response = await schemaApi.getTemplateUsage(templateName);
      if (response.success && response.data) {
        setTemplateUsage(response.data);
      }
    } catch (err) {
      console.error('Failed to load template usage:', err);
    }
  };

  const toggleTemplateExpansion = (templateName: string) => {
    const newExpanded = new Set(expandedTemplates);
    if (newExpanded.has(templateName)) {
      newExpanded.delete(templateName);
    } else {
      newExpanded.add(templateName);
    }
    setExpandedTemplates(newExpanded);
  };

  // Convert visual schema data back to YAML format
  const convertVisualToYaml = (schema: VisualSchemaData): string => {
    const lines: string[] = [];

    // Add classes section
    if (schema.classes.length > 0) {
      lines.push('classes:');

      for (const cls of schema.classes) {
        lines.push(`  ${cls.name}:`);

        if (cls.description) {
          lines.push(`    description: ${cls.description}`);
        }

        if (cls.extends) {
          lines.push(`    is_a: ${cls.extends}`);
        }

        if (cls.abstract) {
          lines.push(`    abstract: true`);
        }

        if (cls.attributes.length > 0) {
          lines.push('    attributes:');

          for (const attr of cls.attributes) {
            lines.push(`      ${attr.name}:`);
            lines.push(`        range: ${attr.type}`);

            if (attr.required) {
              lines.push('        required: true');
            }

            if (attr.multivalued) {
              lines.push('        multivalued: true');
            }

            if (attr.description) {
              lines.push(`        description: ${attr.description}`);
            }

            if (attr.sync_to_square) {
              lines.push('        annotations:');
              lines.push('          sync_to_square: true');
            }
          }
        }
      }
    }

    // Add enums section
    if (schema.enums.length > 0) {
      if (lines.length > 0) {
        lines.push(''); // Blank line between sections
      }
      lines.push('enums:');

      for (const enm of schema.enums) {
        lines.push(`  ${enm.name}:`);

        if (enm.description) {
          lines.push(`    description: ${enm.description}`);
        }

        if (enm.values.length > 0) {
          lines.push('    permissible_values:');
          for (const value of enm.values) {
            lines.push(`      ${value}:`);
          }
        }
      }
    }

    return lines.join('\n');
  };

  // Generate initial YAML for new template
  const generateInitialYaml = (options: {
    name: string;
    title: string;
    description?: string;
    baseTemplate?: string;
    baseTemplateVersion?: string;
    imports?: { name: string; version: string }[];
  }): string => {
    const lines: string[] = [];

    // Header
    lines.push(`id: https://coordinated.services/schemas/${options.name}`);
    lines.push(`name: ${options.name}`);
    lines.push(`title: ${options.title}`);
    if (options.description) {
      lines.push(`description: >-`);
      lines.push(`  ${options.description}`);
    }
    lines.push('');

    // Prefixes (required by LinkML)
    lines.push('prefixes:');
    lines.push('  linkml: https://w3id.org/linkml/');
    lines.push('  schema: http://schema.org/');
    lines.push('');

    // Imports (if base template selected, auto-add to imports)
    const allImports = [...(options.imports || [])];
    if (options.baseTemplate && options.baseTemplateVersion) {
      // Add base template to imports if not already present
      if (!allImports.some(imp => imp.name === options.baseTemplate)) {
        allImports.unshift({
          name: options.baseTemplate,
          version: options.baseTemplateVersion
        });
      }
    }

    if (allImports.length > 0) {
      lines.push('imports:');
      lines.push('  - linkml:types');
      for (const imp of allImports) {
        lines.push(`  - ../${imp.name}/${imp.version}`);
      }
      lines.push('');
    }

    lines.push('default_range: string');
    lines.push('');

    // Classes section
    lines.push('classes:');

    if (options.baseTemplate) {
      // Extend base template's ProductAttributes
      const className = options.title.replace(/\s+/g, '') + 'Attributes';
      lines.push(`  ${className}:`);
      lines.push(`    is_a: ProductAttributes`);
      lines.push(`    description: >-`);
      lines.push(`      ${options.description || 'Custom attributes for ' + options.title}`);
      lines.push('    attributes:');
      lines.push('      # Add your custom attributes here');
      lines.push('      # Example:');
      lines.push('      # custom_field:');
      lines.push('      #   range: string');
      lines.push('      #   required: true');
      lines.push('      #   annotations:');
      lines.push('      #     sync_to_square: "true"');
    } else {
      // Minimal standalone class
      const className = options.title.replace(/\s+/g, '');
      lines.push(`  ${className}:`);
      lines.push(`    description: >-`);
      lines.push(`      ${options.description || options.title}`);
      lines.push('    attributes:');
      lines.push('      # Define your attributes here');
      lines.push('      # Example:');
      lines.push('      # name:');
      lines.push('      #   range: string');
      lines.push('      #   required: true');
    }

    lines.push('');
    lines.push('# Enums section (optional)');
    lines.push('# enums:');
    lines.push('#   MyEnum:');
    lines.push('#     permissible_values:');
    lines.push('#       VALUE1:');
    lines.push('#       VALUE2:');

    return lines.join('\n');
  };

  // Validate create template form
  const validateCreateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!createTemplateName.trim()) {
      errors.name = 'Template name is required';
    } else if (!/^[a-z][a-z0-9_]*$/.test(createTemplateName)) {
      errors.name = 'Name must be lowercase with underscores (e.g., "my_template")';
    } else if (templates.some(t => t.name === createTemplateName)) {
      errors.name = 'A template with this name already exists';
    }

    if (!createTemplateTitle.trim()) {
      errors.title = 'Template title is required';
    }

    setCreateValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create template
  const handleCreateTemplate = async () => {
    if (!validateCreateForm()) return;

    try {
      setCreating(true);
      setError(null);

      // Get versions for base template and imports
      const baseTemplateVersion = selectedBaseTemplate
        ? templates.find(t => t.name === selectedBaseTemplate)?.latest_version
        : undefined;

      const importSchemas = selectedImports.map(name => ({
        name,
        version: templates.find(t => t.name === name)?.latest_version || 'v1.0.0'
      }));

      // Generate initial YAML
      const yamlContent = generateInitialYaml({
        name: createTemplateName,
        title: createTemplateTitle,
        description: createTemplateDescription || undefined,
        baseTemplate: selectedBaseTemplate || undefined,
        baseTemplateVersion,
        imports: importSchemas.length > 0 ? importSchemas : undefined
      });

      // Call API to create template
      const response = await schemaApi.createTemplate({
        name: createTemplateName,
        title: createTemplateTitle,
        tags: createTemplateTags,
        description: createTemplateDescription || '',
        yaml_content: yamlContent
      });

      if (!response.success) {
        setError(response.message || 'Failed to create template');
        return;
      }

      // Success: Close modal, reset form, refresh list
      setCreateModalOpen(false);
      setCreateTemplateName('');
      setCreateTemplateTitle('');
      setCreateTemplateDescription('');
      setCreateTemplateTags([]);
      setSelectedBaseTemplate('');
      setSelectedImports([]);
      setCreateValidationErrors({});

      await loadTemplates();

      // Auto-open edit modal with the new template
      setModalTemplateName(createTemplateName);
      await loadTemplateYaml(createTemplateName);
      setEditModalOpen(true);

    } catch (err) {
      console.error('Failed to create template:', err);
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const loadTemplateYaml = async (templateName: string) => {
    try {
      const response = await schemaApi.getBuiltin(templateName);
      if (response.success && response.data) {
        const yamlContent = response.data.yaml_content;
        setTemplateYaml(yamlContent);

        // Use the properly parsed JSON from backend instead of regex parsing
        const classes: VisualSchemaClass[] = [];
        const enums: VisualSchemaEnum[] = [];

        // Convert backend classes to visual format
        if (response.data.classes) {
          for (const [className, classData] of Object.entries(response.data.classes)) {
            const visualAttrs: VisualSchemaAttribute[] = [];

            // Extract attributes from class data (filter out is_a which is not an attribute)
            if (typeof classData === 'object' && classData !== null) {
              for (const [key, attrData] of Object.entries(classData as Record<string, any>)) {
                // Skip is_a - it's not an attribute, it's inheritance info
                if (key === 'is_a') continue;

                // Check if this is actually an attribute (has range/type info)
                if (typeof attrData === 'object' && attrData !== null) {
                  visualAttrs.push({
                    name: key,
                    type: attrData.range || 'string',
                    required: attrData.required || false,
                    source: 'builtin' as const
                  });
                }
              }
            }

            classes.push({
              name: className,
              attributes: visualAttrs,
              source: 'builtin' as const,
              extends: (classData as any).is_a || undefined  // Extract inheritance
            });
          }
        }

        // Convert backend enums to visual format
        if (response.data.enums) {
          for (const [enumName, enumValues] of Object.entries(response.data.enums)) {
            const values: string[] = [];

            if (Array.isArray(enumValues)) {
              for (const val of enumValues) {
                if (typeof val === 'object' && val !== null && 'value' in val) {
                  values.push(val.value);
                } else if (typeof val === 'string') {
                  values.push(val);
                }
              }
            }

            enums.push({
              name: enumName,
              values,
              source: 'builtin' as const
            });
          }
        }

        setVisualSchemaData({ classes, enums, categories: [] });
        return yamlContent;
      }
    } catch (err) {
      console.error('Failed to load template YAML:', err);
    }
    return '';
  };

  const handleViewTemplate = async (templateName: string) => {
    setModalTemplateName(templateName);
    const yaml = await loadTemplateYaml(templateName);
    setTemplateYaml(yaml);
    setViewModalOpen(true);
  };

  const handleEditTemplate = async (templateName: string) => {
    setModalTemplateName(templateName);
    const yaml = await loadTemplateYaml(templateName);
    setTemplateYaml(yaml);
    setEditModalOpen(true);
  };

  const handleRenameTemplate = (templateName: string) => {
    setRenameTemplateName(templateName);
    setNewTemplateName(templateName);
    setRenameModalOpen(true);
    setContextMenuTemplate(null);
  };

  const handleDeleteTemplate = (templateName: string) => {
    setDeleteTemplateName(templateName);
    setDeleteModalOpen(true);
    setContextMenuTemplate(null);
  };

  const confirmRename = async () => {
    if (!newTemplateName.trim() || newTemplateName === renameTemplateName) {
      setRenameModalOpen(false);
      return;
    }

    try {
      // TODO: Implement API call to rename template
      console.log('Rename template from', renameTemplateName, 'to', newTemplateName);

      // Refresh templates list
      await loadTemplates();

      setRenameModalOpen(false);
      setRenameTemplateName('');
      setNewTemplateName('');
    } catch (err) {
      console.error('Failed to rename template:', err);
      setError(err instanceof Error ? err.message : 'Failed to rename template');
    }
  };

  const confirmDelete = async () => {
    try {
      setError(null);

      // Call API to delete template
      const response = await schemaApi.deleteTemplate(deleteTemplateName);

      if (!response.success) {
        setError(response.message || 'Failed to delete template');
        return;
      }

      // Refresh templates list
      await loadTemplates();

      setDeleteModalOpen(false);
      setDeleteTemplateName('');
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleAssignTemplate = async (templateName: string) => {
    setAssignTemplateName(templateName);

    // Load available organizations
    try {
      // TODO: Implement API call to fetch organizations
      // For now, using mock data
      const mockOrgs = [
        { id: '1', name: 'WindingRiver Holdings' },
        { id: '2', name: 'Koshinko' },
        { id: '3', name: 'Test Organization' },
      ];
      setAvailableOrganizations(mockOrgs);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }

    setAssignModalOpen(true);
  };

  const confirmAssignTemplate = async () => {
    if (selectedOrganizations.length === 0) {
      setAssignModalOpen(false);
      return;
    }

    try {
      // TODO: Implement API call to assign template to organizations
      console.log('Assign template', assignTemplateName, 'to organizations:', selectedOrganizations);

      // Refresh usage data
      if (selectedTemplate) {
        await loadTemplateUsage(selectedTemplate);
      }

      setAssignModalOpen(false);
      setAssignTemplateName('');
      setSelectedOrganizations([]);
    } catch (err) {
      console.error('Failed to assign template:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign template');
    }
  };

  const toggleOrganizationSelection = (orgId: string) => {
    setSelectedOrganizations(prev =>
      prev.includes(orgId)
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const validateYamlSchema = (yamlContent: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Basic YAML structure validation
    if (!yamlContent.trim()) {
      errors.push('Schema cannot be empty');
      return { isValid: false, errors };
    }

    // Parse YAML to object
    let parsed: any;
    try {
      parsed = yaml.load(yamlContent);
    } catch (e) {
      if (e instanceof Error) {
        errors.push(`YAML syntax error: ${e.message}`);
      } else {
        errors.push('Invalid YAML syntax');
      }
      return { isValid: false, errors };
    }

    // Check that parsed result is an object
    if (!parsed || typeof parsed !== 'object') {
      errors.push('Schema must be a YAML object');
      return { isValid: false, errors };
    }

    // Check for required top-level sections
    const hasClasses = 'classes' in parsed && typeof parsed.classes === 'object';
    const hasEnums = 'enums' in parsed && typeof parsed.enums === 'object';
    console.log('[YAML Validation] Has classes section?', hasClasses, 'Has enums section?', hasEnums);

    if (!hasClasses && !hasEnums) {
      errors.push('Schema must have at least one of: classes or enums section');
    }

    // Check for tabs in YAML (YAML spec requires spaces)
    const lines = yamlContent.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('\t')) {
        errors.push(`Line ${i + 1}: YAML must use spaces, not tabs`);
      }
    }

    // Get list of defined classes and enums
    const definedClasses = new Set<string>();
    const definedEnums = new Set<string>();

    if (hasClasses) {
      const classes = parsed.classes;
      if (classes && typeof classes === 'object') {
        for (const className in classes) {
          if (typeof className === 'string' && /^[A-Za-z]\w*$/.test(className)) {
            definedClasses.add(className);
          }
        }

        if (Object.keys(classes).length > 0 && definedClasses.size === 0) {
          errors.push('classes section has no valid class definitions (class names must start with a letter)');
        }
      }
    }

    if (hasEnums) {
      const enums = parsed.enums;
      if (enums && typeof enums === 'object') {
        for (const enumName in enums) {
          if (typeof enumName === 'string' && /^[A-Za-z]\w*$/.test(enumName)) {
            definedEnums.add(enumName);
          }
        }

        if (Object.keys(enums).length > 0 && definedEnums.size === 0) {
          errors.push('enums section has no valid enum definitions (enum names must start with a letter)');
        }
      }
    }

    console.log('[YAML Validation] Defined classes:', Array.from(definedClasses));
    console.log('[YAML Validation] Defined enums:', Array.from(definedEnums));

    // Check for broken references in classes
    if (hasClasses) {
      const classes = parsed.classes;
      const builtinTypes = ['String', 'Integer', 'Float', 'Boolean', 'Date', 'DateTime', 'Uri', 'Uriorcurie', 'string', 'integer', 'float', 'boolean', 'date', 'datetime', 'uri', 'uriorcurie'];

      for (const className in classes) {
        const classData = classes[className];
        if (typeof classData !== 'object' || classData === null) continue;

        // Check is_a (inheritance)
        if ('is_a' in classData && typeof classData.is_a === 'string') {
          const referencedClass = classData.is_a;
          if (!definedClasses.has(referencedClass)) {
            errors.push(`Class inherits from undefined class: ${referencedClass}`);
          }
        }

        // Check attributes range references
        if ('attributes' in classData && typeof classData.attributes === 'object' && classData.attributes !== null) {
          for (const attrName in classData.attributes) {
            const attrData = classData.attributes[attrName];
            if (typeof attrData === 'object' && attrData !== null && 'range' in attrData) {
              const referencedType = attrData.range;
              if (typeof referencedType === 'string') {
                // Skip built-in types (case-insensitive)
                if (builtinTypes.includes(referencedType)) continue;

                // Check if it's a defined class or enum
                if (!definedClasses.has(referencedType) && !definedEnums.has(referencedType)) {
                  errors.push(`Attribute references undefined type: ${referencedType}`);
                }
              }
            }
          }
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleSaveChanges = async () => {
    setValidationErrors([]);
    setIsSaving(true);

    try {
      // Validate YAML before saving
      const validation = validateYamlSchema(templateYaml);

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setIsSaving(false);
        return;
      }

      // Save template by creating a new version
      const response = await schemaApi.createTemplateVersion(modalTemplateName, {
        version: 'auto', // Backend will auto-increment version
        yaml_content: templateYaml
      });

      if (!response.success) {
        setValidationErrors([response.message || 'Failed to save template']);
        setIsSaving(false);
        return;
      }

      // Close modal on success
      setEditModalOpen(false);
      setIsFullScreen(false);
      setValidationErrors([]);

      // Refresh templates list
      await loadTemplates();

    } catch (err) {
      console.error('Failed to save template:', err);
      setValidationErrors([err instanceof Error ? err.message : 'Failed to save template']);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-50 border-red-200">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
          <div>
            <h3 className="text-lg font-semibold text-red-900">Error</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Global Schema Templates</h1>
          <p className="text-gray-600 mt-2">
            Manage schema templates and assign them to organizations
          </p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          New Template
        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {templates.map((template) => {
          const isExpanded = expandedTemplates.has(template.name);
          const usageCount = templateUsage?.template_name === template.name
            ? templateUsage.organizations.length
            : 0;

          return (
            <Card
              key={template.name}
              className={`transition-all cursor-pointer ${
                selectedTemplate === template.name
                  ? 'ring-2 ring-blue-500 shadow-lg'
                  : 'hover:shadow-md'
              }`}
              onClick={() => setSelectedTemplate(template.name)}
            >
              <div className="space-y-4">
                {/* Template Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {template.title || template.name}
                      </h3>
                      <p className="text-sm text-gray-500">{template.name}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {/* Context Menu Button */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuTemplate(
                            contextMenuTemplate === template.name ? null : template.name
                          );
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                        data-testid={`context-menu-button-${template.name}`}
                        aria-label="Template options"
                        title="Template options"
                      >
                        <MoreVertical className="h-5 w-5 text-gray-400" />
                      </button>

                      {/* Context Menu Dropdown */}
                      {contextMenuTemplate === template.name && (
                        <div
                          className="absolute right-0 top-8 z-10 bg-white shadow-lg rounded-lg border border-gray-200 py-1 min-w-[160px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleRenameTemplate(template.name)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-gray-700"
                            data-testid={`rename-button-${template.name}`}
                          >
                            <Type className="h-4 w-4" />
                            Rename
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteTemplate(template.name)}
                            className="w-full text-left px-4 py-2 hover:bg-red-50 flex items-center gap-2 text-red-600"
                            data-testid={`delete-button-${template.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Expand/Collapse Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTemplateExpansion(template.name);
                      }}
                      className="p-1 hover:bg-gray-100 rounded"
                      aria-label={isExpanded ? 'Collapse template' : 'Expand template'}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Template Info */}
                <div className="space-y-2">
                  {template.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {template.description}
                    </p>
                  )}

                  {/* Tags */}
                  {template.tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="h-4 w-4 text-gray-400" />
                      {template.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Version: {template.latest_version}</span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {usageCount} {usageCount === 1 ? 'org' : 'orgs'}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{template.class_count} classes</span>
                    <span>{template.enum_count} enums</span>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t pt-4 space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Versions ({template.versions.length})
                      </h4>
                      <div className="space-y-1">
                        {template.versions.slice(0, 3).map((version) => (
                          <div
                            key={version.version}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-700">
                                {version.version}
                              </span>
                              {version.is_latest && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                  Latest
                                </span>
                              )}
                            </div>
                            {version.created_at && (
                              <span className="text-gray-500 text-xs flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(version.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                        {template.versions.length > 3 && (
                          <p className="text-xs text-gray-500">
                            +{template.versions.length - 3} more versions
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewTemplate(template.name);
                        }}
                        className="flex items-center gap-1 flex-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTemplate(template.name);
                        }}
                        className="flex items-center gap-1 flex-1"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Template Details Panel */}
      {selectedTemplate && templateUsage && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  Organizations using {selectedTemplate}
                </h3>
                <p className="text-blue-700 text-sm mt-1">
                  Version: {templateUsage.version}
                </p>
              </div>
              <Button
                onClick={() => handleAssignTemplate(selectedTemplate)}
                size="sm"
              >
                Assign to Organizations
              </Button>
            </div>

            {templateUsage.organizations.length > 0 ? (
              <div className="space-y-2">
                {templateUsage.organizations.map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between bg-white p-3 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{org.name}</p>
                        <p className="text-sm text-gray-500">ID: {org.id}</p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Assigned: {new Date(org.assigned_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No organizations assigned to this template yet</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {templates.length === 0 && (
        <Card className="text-center py-12">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No templates found
          </h3>
          <p className="text-gray-600 mb-6">
            Get started by creating your first schema template
          </p>
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Create Template
          </Button>
        </Card>
      )}


      {/* Create Template Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="border-b px-6 py-4 bg-blue-50">
              <h2 className="text-xl font-semibold text-blue-900">Create New Template</h2>
              <p className="text-sm text-blue-700 mt-1">
                Define a new schema template with optional base template and imports
              </p>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Metadata Section */}
                <div>
                  <h3 className="text-md font-semibold text-gray-900 mb-4">Template Metadata</h3>
                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={createTemplateName}
                        onChange={(e) => {
                          setCreateTemplateName(e.target.value);
                          if (createValidationErrors.name) {
                            setCreateValidationErrors(prev => ({ ...prev, name: '' }));
                          }
                        }}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                          createValidationErrors.name
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                        placeholder="my_template (lowercase with underscores)"
                        autoFocus
                      />
                      {createValidationErrors.name && (
                        <p className="text-sm text-red-600 mt-1">{createValidationErrors.name}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Used as folder name and schema ID. Must be lowercase with underscores.
                      </p>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={createTemplateTitle}
                        onChange={(e) => {
                          setCreateTemplateTitle(e.target.value);
                          if (createValidationErrors.title) {
                            setCreateValidationErrors(prev => ({ ...prev, title: '' }));
                          }
                        }}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                          createValidationErrors.title
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                        placeholder="My Template (human-readable)"
                      />
                      {createValidationErrors.title && (
                        <p className="text-sm text-red-600 mt-1">{createValidationErrors.title}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Human-readable display name for the template.
                      </p>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={createTemplateDescription}
                        onChange={(e) => setCreateTemplateDescription(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={3}
                        placeholder="Describe what this template is used for..."
                      />
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags
                      </label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                              e.preventDefault();
                              const tag = e.currentTarget.value.trim();
                              if (!createTemplateTags.includes(tag)) {
                                setCreateTemplateTags([...createTemplateTags, tag]);
                              }
                              e.currentTarget.value = '';
                            }
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Press Enter to add tag..."
                        />
                      </div>
                      {createTemplateTags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {createTemplateTags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => setCreateTemplateTags(createTemplateTags.filter((_, i) => i !== idx))}
                                className="hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Structure Section */}
                <div className="border-t pt-6">
                  <h3 className="text-md font-semibold text-gray-900 mb-4">Template Structure</h3>
                  <div className="space-y-4">
                    {/* Base Template */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Base Template (Optional)
                      </label>
                      <select
                        value={selectedBaseTemplate}
                        onChange={(e) => {
                          setSelectedBaseTemplate(e.target.value);
                          // Remove base template from imports if it's selected there
                          if (e.target.value && selectedImports.includes(e.target.value)) {
                            setSelectedImports(selectedImports.filter(imp => imp !== e.target.value));
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="Select base template"
                      >
                        <option value="">None (start from scratch)</option>
                        {templates.map((template) => (
                          <option key={template.name} value={template.name}>
                            {template.title || template.name} (v{template.latest_version})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Your template will inherit from this base using <code className="bg-gray-100 px-1">is_a:</code> syntax
                      </p>
                    </div>

                    {/* Additional Imports */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Imports (Optional)
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                        {templates.length > 0 ? (
                          <div className="space-y-2">
                            {templates
                              .filter(t => t.name !== selectedBaseTemplate) // Exclude base template
                              .map((template) => (
                                <label
                                  key={template.name}
                                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedImports.includes(template.name)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedImports([...selectedImports, template.name]);
                                      } else {
                                        setSelectedImports(selectedImports.filter(imp => imp !== template.name));
                                      }
                                    }}
                                    className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-900">
                                    {template.title || template.name}
                                    <span className="text-gray-500 ml-1">(v{template.latest_version})</span>
                                  </span>
                                </label>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No templates available for import</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Import schemas to use their types and classes via <code className="bg-gray-100 px-1">imports:</code> directive
                      </p>
                    </div>

                    {/* Preview */}
                    {(selectedBaseTemplate || selectedImports.length > 0) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-900 mb-2">Structure Preview</h4>
                        <div className="text-sm text-blue-800 space-y-1">
                          {selectedBaseTemplate && (
                            <div>
                              <strong>Inherits from:</strong> {selectedBaseTemplate}
                            </div>
                          )}
                          {selectedImports.length > 0 && (
                            <div>
                              <strong>Imports:</strong> {selectedImports.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-between items-center bg-gray-50">
              <div className="text-sm text-gray-600">
                {Object.keys(createValidationErrors).length > 0 && (
                  <span className="text-red-600 font-medium">
                    Please fix validation errors
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreateModalOpen(false);
                    setCreateTemplateName('');
                    setCreateTemplateTitle('');
                    setCreateTemplateDescription('');
                    setCreateTemplateTags([]);
                    setSelectedBaseTemplate('');
                    setSelectedImports([]);
                    setCreateValidationErrors({});
                  }}
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTemplate}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Template'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModalOpen && modalTemplateName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">View Template: {modalTemplateName}</h2>
                <p className="text-sm text-gray-600 mt-1">Read-only YAML content</p>
              </div>
              <button
                type="button"
                onClick={() => setViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm font-mono overflow-x-auto">
                {templateYaml}
              </pre>
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-end">
              <Button onClick={() => setViewModalOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-900">Rename Template</h2>
              <p className="text-sm text-gray-600 mt-1">
                Enter a new name for the template <span className="font-mono text-gray-900">{renameTemplateName}</span>
              </p>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Template Name
              </label>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new name..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    confirmRename();
                  } else if (e.key === 'Escape') {
                    setRenameModalOpen(false);
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-2">
                This will rename the template and all its versions. Organizations using this template will continue to work with the new name.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setRenameModalOpen(false);
                  setRenameTemplateName('');
                  setNewTemplateName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRename}
                disabled={!newTemplateName.trim() || newTemplateName === renameTemplateName}
              >
                Rename Template
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="border-b px-6 py-4 bg-red-50">
              <h2 className="text-xl font-semibold text-red-900 flex items-center gap-2">
                <AlertCircle className="h-6 w-6" />
                Delete Template
              </h2>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-900 mb-4">
                Are you sure you want to delete the template{' '}
                <span className="font-mono font-semibold text-red-600">{deleteTemplateName}</span>?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2">
                <p className="text-sm text-yellow-900 font-semibold">⚠️ Warning: This action cannot be undone</p>
                <ul className="text-sm text-yellow-800 space-y-1 ml-4 list-disc">
                  <li>All versions of this template will be deleted</li>
                  <li>Organizations using this template may be affected</li>
                  <li>Consider reassigning organizations to a different template first</li>
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteTemplateName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Template
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Organizations Modal */}
      {assignModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="border-b px-6 py-4 bg-blue-50">
              <h2 className="text-xl font-semibold text-blue-900">Assign Template to Organizations</h2>
              <p className="text-sm text-blue-700 mt-1">
                Template: <span className="font-mono font-semibold">{assignTemplateName}</span>
              </p>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-gray-700 mb-4">
                Select organizations to assign this template to. Organizations will use this template for their product schema.
              </p>

              {availableOrganizations.length > 0 ? (
                <div className="space-y-2">
                  {availableOrganizations.map((org) => (
                    <label
                      key={org.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedOrganizations.includes(org.id)
                          ? 'bg-blue-50 border-blue-500'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrganizations.includes(org.id)}
                        onChange={() => toggleOrganizationSelection(org.id)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{org.name}</div>
                        <div className="text-sm text-gray-500">ID: {org.id}</div>
                      </div>
                      {selectedOrganizations.includes(org.id) && (
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>No organizations available</p>
                </div>
              )}

              {selectedOrganizations.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-semibold mb-2">
                    Selected: {selectedOrganizations.length} organization{selectedOrganizations.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-blue-700">
                    These organizations will be assigned the template "{assignTemplateName}". Existing schemas will be replaced.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-between items-center bg-gray-50">
              <div className="text-sm text-gray-600">
                {selectedOrganizations.length === 0 ? (
                  <span>Select at least one organization</span>
                ) : (
                  <span className="text-blue-700 font-medium">
                    {selectedOrganizations.length} selected
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAssignModalOpen(false);
                    setAssignTemplateName('');
                    setSelectedOrganizations([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmAssignTemplate}
                  disabled={selectedOrganizations.length === 0}
                >
                  Assign Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && modalTemplateName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`bg-white rounded-lg shadow-xl overflow-hidden flex flex-col transition-all duration-300 ${
              isFullScreen
                ? 'w-full h-full max-w-none max-h-none'
                : 'max-w-6xl w-full max-h-[90vh]'
            }`}
          >
            {/* Modal Header */}
            <div className="border-b px-6 py-4 flex items-center justify-between bg-gray-50">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">Edit Template: {modalTemplateName}</h2>
                <p className="text-sm text-gray-600 mt-1">Modify schema using visual editor or YAML</p>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2 mr-4">
                <button
                  type="button"
                  onClick={() => setEditMode('visual')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    editMode === 'visual'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="visual-mode-button"
                >
                  <Box className="h-4 w-4" />
                  Visual
                </button>
                <button
                  type="button"
                  onClick={() => setEditMode('yaml')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    editMode === 'yaml'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="yaml-mode-button"
                >
                  <Code className="h-4 w-4" />
                  YAML
                </button>
              </div>

              {/* Fullscreen Toggle */}
              <button
                type="button"
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg mr-2 transition-colors"
                title={isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullScreen ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setEditModalOpen(false);
                  setIsFullScreen(false);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {editMode === 'visual' ? (
                <div
                  data-testid="visual-schema-editor"
                  className={`${isFullScreen ? 'h-full' : 'h-[600px]'}`}
                >
                  <VisualSchemaBuilder
                    schema={visualSchemaData}
                    schemaType="builtin"
                    onChange={(newSchema) => {
                      setVisualSchemaData(newSchema);
                      // Convert visual schema back to YAML
                      const updatedYaml = convertVisualToYaml(newSchema);
                      setTemplateYaml(updatedYaml);
                    }}
                  />
                </div>
              ) : (
                <textarea
                  data-testid="yaml-editor"
                  value={templateYaml}
                  onChange={(e) => {
                    const newYaml = e.target.value;
                    setTemplateYaml(newYaml);

                    // Real-time validation - this will clear backend errors when user edits
                    const validation = validateYamlSchema(newYaml);
                    setValidationErrors(validation.errors);

                    // Clear the saving state if user is editing after a failed save
                    if (isSaving) {
                      setIsSaving(false);
                    }
                  }}
                  className={`w-full bg-gray-50 p-4 rounded-lg border text-sm font-mono resize-none focus:outline-none focus:ring-2 ${
                    validationErrors.length > 0
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-200 focus:ring-blue-500'
                  } ${isFullScreen ? 'h-full' : 'h-96'}`}
                  placeholder="YAML content..."
                />
              )}
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="border-t border-red-200 bg-red-50 px-6 py-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-900 mb-2">
                      Validation Errors ({validationErrors.length})
                    </h4>
                    <ul className="space-y-1 text-sm text-red-700">
                      {validationErrors.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-red-500">•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-between items-center bg-gray-50">
              <div className="text-sm text-gray-600">
                {validationErrors.length > 0 && (
                  <span className="text-red-600 font-medium">
                    Fix validation errors before saving
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditModalOpen(false);
                    setIsFullScreen(false);
                    setValidationErrors([]);
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving || validationErrors.length > 0}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
